import withLayout from '@/components/hocs/withStoreLayout'
import getHomeContent from '@/core/static-props/banner'
import { searchService } from '@/domain/Search'
import { storeService } from '@/domain/Store'
import { CMS } from '@/shared/types/banner'
import formatAmount from '@/utils/format-amount'
import {
    Anchor,
    Box,
    Button,
    Group,
    Rating,
    Skeleton,
    Space,
    Stack,
    Text,
    Title,
    clsx,
    useMantineTheme,
} from '@mantine/core'
import { useEffect, useRef, useState } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import {
    ArrowRight,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'
import Link from 'next/link'
import {
    GetServerSideProps,
    GetServerSidePropsContext,
    InferGetServerSidePropsType,
} from 'next/types'
import { RetrieveProductTemplate } from 'stoux-shared-types/services/search'
import { StoreWithAverageRating } from 'stoux-shared-types/services/store'
import { getStore } from '@/utils/store'
import { SeoHead, SeoProps } from '@/shared/seo/SeoHead'
import ImageWithFallback from '@/components/ui/ImageWithFallback'
import { motion } from 'motion/react'
import { IsMobile } from '@/utils/mobile'

const metadata: SeoProps = {
    title: 'Explorez une grande collection de jeux-vidéos et de consoles sur STOUX',
    description:
        'STOUX est une plateforme de vente de jeux-vidéos et de consoles. Découvrez une grande collection de jeux-vidéos et de consoles sur STOUX.',
}

export const getServerSideProps = (async ({
    req,
    res,
}: GetServerSidePropsContext) => {
    const store = getStore()

    const _content = getHomeContent(store)
    const _search = store
        ? searchService.getSearchResults({
              storeId: store,
              sortBy: 'latest',
              itemsPerPage: 10,
              showOutOfStock: null,
              page: 1,
              minPrice: null,
          })
        : searchService.getSearchResults({
              itemsPerPage: 10,
              sortBy: 'homepage', // hack for the API to understand that it's the homepage search
              storeId: store,
          })

    const _vendors = storeService.list()

    const [{ banner }, searchResult, vendors] = await Promise.all([
        _content,
        _search,
        _vendors,
    ])

    const MAX_AGE = 10 * 60 // 10 minutes : les nouveautés doivent refléter le stock réel
    res.setHeader(
        'Cache-Control',
        `public, s-maxage=${MAX_AGE} , stale-while-revalidate=${MAX_AGE}`,
    )

    const products = searchResult?.items
   
    return {
        props: {
            banner: banner || null,
            products,
            vendors: store ? null : vendors,
        },
    }
}) as GetServerSideProps<{
    banner?: CMS | Partial<CMS> | null
    products?: RetrieveProductTemplate[] | null
    vendors?: StoreWithAverageRating[] | null
}>

const HomePage = ({
    banner,
    products,
    vendors,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    return (
        <>
            <SeoHead {...metadata} />
            <section
                id="banner"
                className="relative my-8 overflow-hidden bg-white"
            >
                <div aria-hidden="true" className="absolute inset-0 z-0">
                    <div
                        className={clsx(
                            'absolute inset-0 mx-auto overflow-hidden max-w-7xl xl:px-8',
                            !banner?.resource_url && 'bg-white',
                        )}
                    >
                        {banner?.resource_url &&
                            banner.resource_url !== 'null' && (
                                <ImageWithFallback
                                    src={
                                        banner?.resource_url ||
                                        '/images/hero.png'
                                    }
                                    alt={
                                        (banner?.metadata?.title as string) ||
                                        'hero'
                                    }
                                    className="object-cover object-center w-full h-full rounded"
                                    loading="eager"
                                    width={1920}
                                    height={1080}
                                />
                            )}
                    </div>
                </div>

                {/* Banner */}
                <section
                    aria-labelledby="sale-heading"
                    className="relative flex flex-col items-center px-4 pt-32 mx-auto text-center bg-white bg-opacity-25 max-w-7xl sm:px-6 lg:px-8"
                >
                    <div className="max-w-2xl mx-auto lg:max-w-none">
                        <h2
                            id="sale-heading"
                            className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl"
                        >
                            {banner?.metadata?.title as string}
                        </h2>
                        <p className="max-w-xl mx-auto mt-4 text-xl text-gray-600">
                            {banner?.metadata?.content as string}
                        </p>
                        {(banner?.metadata?.url as string | null) &&
                            (banner?.metadata?.button_text as
                                | string
                                | null) && (
                                <Anchor
                                    href={banner?.metadata?.url as string}
                                    className="inline-block w-full px-8 py-3 mt-6 mb-4 font-medium text-white bg-[var(--primary)] border border-transparent rounded-md hover:bg-primary-dark sm:w-auto"
                                >
                                    {banner?.metadata?.button_text as string}
                                </Anchor>
                            )}
                    </div>
                </section>
            </section>

        <section id="new-products" className="block w-full my-24 xl:my-32">
            <div className="flex flex-col items-center text-center mb-8 px-2">
                <Stack spacing={4} className="items-center">
                    <Title order={2}>
                        Nos <span className="text-primary">nouveautés</span>
                    </Title>
                    <Text color="gray.6" size="sm">
                        Découvrez les derniers produits ajoutés à notre catalogue.
                    </Text>
                </Stack>
                <Anchor href="/search?sortBy=latest" size="sm" color="gray" className="flex items-center gap-1 hover:text-primary transition-colors mt-2">
                    Voir tout <ArrowRight size={14} />
                </Anchor>
            </div>
            <NewProducts products={products || []} />
        </section>

            {vendors && (
                <section className="block w-full my-24 xl:my-32">
                    <Stack className="mb-8 text-center" spacing="xs">
                        <Title order={2}>
                            Les boutiques{' '}
                            <span className="text-primary">en ligne</span>
                        </Title>
                        <Text color="gray.6">
                            Découvrez les boutiques en ligne de nos vendeurs!
                        </Text>
                    </Stack>
                    <div className="relative overflow-hidden bg-white">
                        <div className="pt-16 pb-80 sm:pb-40 sm:pt-24 lg:pb-10 lg:pt-10">
                            {/* Full width: stores stay on one line as long as the screen allows */}
                            <div className="relative w-full px-4 sm:static sm:px-6 lg:px-8">
                                <VendorsList vendors={vendors || []} />
                            </div>
                        </div>
                    </div>
                </section>
            )}
        </>
    )
}

const NewProductsLoading = () => {
    const { colors } = useMantineTheme()
    return (
        <div className="flex p-8 overflow-x-auto overflow-y-hidden gap-x-8 gap-y-10 whitespace-nowrap scrollbar-hide no-scrollbar md:gap-x-10 lg:gap-x-12 xl:gap-x-14 2xl:gap-x-16 md:gap-y-12 lg:gap-y-14 xl:gap-y-16 2xl:gap-y-18 ">
            <Skeleton className="w-full aspect-square" />
            <Skeleton className="w-full aspect-square" />
            <Skeleton className="w-full aspect-square" />
            <Skeleton className="w-full aspect-square" />
            <Skeleton className="w-full aspect-square" />
            <Skeleton className="w-full aspect-square" />
        </div>
    )
}



const NewProducts = ({ products }: { products: RetrieveProductTemplate[] }) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef({ isDown: false, startX: 0, scrollLeft: 0 })
    const hasDraggedRef = useRef(false)
    const [canPrev, setCanPrev] = useState(false)
    const [canNext, setCanNext] = useState(true)
    const isMobile = IsMobile()

    const CARD_WIDTH = isMobile ? 160 : 220
    const CARD_GAP = isMobile ? 5 : 16
    const scrollStep = (CARD_WIDTH + CARD_GAP) * 2

    const updateButtons = () => {
        const el = scrollRef.current
        if (!el) return
        setCanPrev(el.scrollLeft > 0)
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
    }

    // Arrows hidden when every card already fits (list centered, no scroll)
    useEffect(() => {
        updateButtons()
        window.addEventListener('resize', updateButtons)
        return () => window.removeEventListener('resize', updateButtons)
    }, [products])

    const prev = () => scrollRef.current?.scrollBy({ left: -scrollStep, behavior: 'smooth' })
    const next = () => scrollRef.current?.scrollBy({ left: scrollStep, behavior: 'smooth' })

    const onMouseDown = (e: React.MouseEvent) => {
        const el = scrollRef.current
        if (!el) return
        hasDraggedRef.current = false
        dragRef.current = { isDown: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft }
        el.style.cursor = 'grabbing'
    }
    const onMouseLeave = () => {
        dragRef.current.isDown = false
        if (scrollRef.current) scrollRef.current.style.cursor = 'grab'
    }
    const onMouseUp = () => {
        dragRef.current.isDown = false
        if (scrollRef.current) scrollRef.current.style.cursor = 'grab'
    }
    const onMouseMove = (e: React.MouseEvent) => {
        if (!dragRef.current.isDown || !scrollRef.current) return
        e.preventDefault()
        const x = e.pageX - scrollRef.current.offsetLeft
        const walk = (x - dragRef.current.startX) * 1.5
        if (Math.abs(walk) > 5) hasDraggedRef.current = true
        scrollRef.current.scrollLeft = dragRef.current.scrollLeft - walk
    }
    const onClickCapture = (e: React.MouseEvent) => {
        if (hasDraggedRef.current) {
            e.stopPropagation()
            e.preventDefault()
            hasDraggedRef.current = false
        }
    }

    if (!products?.length) return (
        <div className="flex items-center justify-center w-full min-h-[20rem] text-neutral-400 text-sm rounded-2xl bg-gray-50">
            Aucune nouveauté pour le moment.
        </div>
    )

    return (
        <div className="relative pb-4">
            <div
                ref={scrollRef}
                className="overflow-x-auto pb-3"
                style={{ cursor: 'grab' }}
                onScroll={updateButtons}
                onMouseDown={onMouseDown}
                onMouseLeave={onMouseLeave}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
                onClickCapture={onClickCapture}
            >
                {/* Centered while narrower than the screen, scrolls otherwise
                    (justify-center on the scroller would hide the first cards) */}
                <div
                    className="flex w-max mx-auto"
                    style={{ gap: `${CARD_GAP}px` }}
                >
                    {products.map((template) => (
                        <div
                            key={template.id}
                            className="flex-shrink-0 flex flex-col"
                            style={{ width: `${CARD_WIDTH}px` }}
                        >
                            <FeaturedCard {...template} />
                        </div>
                    ))}
                </div>
            </div>

            {!isMobile && canPrev && (
                <button
                    onClick={prev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-2 border border-gray-100 hover:bg-gray-50 transition-colors"
                >
                    <ChevronLeft size={20} className="text-gray-600" />
                </button>
            )}
            {!isMobile && canNext && (
                <button
                    onClick={next}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-2 border border-gray-100 hover:bg-gray-50 transition-colors"
                >
                    <ChevronRight size={20} className="text-gray-600" />
                </button>
            )}
        </div>
    )
}

function useVendors() {
    const query = useQuery({
        queryKey: ['vendors'],
        queryFn: () => storeService.list()
    })
    const vendors = query.data

    return {
        ...query,
        vendors,
    }
}
const VendorsList = ({ vendors }: { vendors: StoreWithAverageRating[] }) => {
    return (
        // Wraps to a new line when the stores no longer fit, every line centered
        <div className="flex flex-wrap justify-center gap-4 transition-all duration-300 sm:gap-6 lg:gap-8">
            {!!vendors?.length &&
                vendors?.map((vendor) => (
                    <div
                        key={vendor.id}
                        // 2 per line on mobile, fixed size above
                        className="w-[calc(50%-0.5rem)] sm:w-44 lg:w-48"
                    >
                        <VendorCard {...vendor} />
                    </div>
                ))}
        </div>
    )
}

const FeaturedCard = ({
    title,
    barcode,
    category_mpath,
    thumbnail,
    lowest_product,
}: RetrieveProductTemplate) => {
    const categories = category_mpath
        .split('/')
        .filter(Boolean)
        .map(seg => seg.replace(/^\d{2}-/, '').replace(/-/g, ' '))

    return (
        <motion.div
            className="relative flex flex-col h-full rounded-2xl overflow-hidden bg-white border border-gray-100 hover:shadow-xl transition-shadow duration-300"
            whileHover="hovered"
        >
            <Link href={`/p/${barcode}`} className="absolute inset-0 z-10" />

            {/* Badge Nouveau */}
            <span className="absolute top-2 left-2 z-20 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-widest">
                Nouveau
            </span>

            {/* Image */}
            <div className="relative w-full aspect-square  overflow-hidden">
                <motion.div
                    className="absolute inset-0"
                    variants={{
                        hovered: { scale: 1.06, transition: { duration: 0.3, ease: 'easeOut' } },
                    }}
                >
                    <ImageWithFallback
                        fill
                        src={(thumbnail !== 'null' && !!thumbnail && thumbnail) || '/images/products/placeholder.png'}
                        alt={title}
                        loading="lazy"
                        className="object-contain p-3"
                        
                    />
                </motion.div>
                {/* Gradient overlay bas */}
                <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white/60 to-transparent pointer-events-none" />
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-3 gap-1.5">
                {/* Catégories en bulles oranges */}
                <div className="flex flex-wrap gap-1">
                    {IsMobile() && categories.length > 0 && (
                        categories.slice(-2).map((cat, i) => (
                            <span
                                key={i}
                                style={{ fontWeight: "bold" }}
                                className="capitalize w-fit bg-orange-300/10 border border-solid border-orange-300 text-orange-500 text-[10px] px-1 py-0 rounded-full"
                            >
                                {cat}
                            </span>
                        ))
                    )}
                    
                    {!IsMobile() && categories.map((cat, i) => (
                        <span
                            key={i}
                            style={{ fontWeight: "bold" }}
                             className="capitalize w-fit bg-orange-300/10 border border-solid border-orange-300 text-orange-500 text-[10px] px-1 py-0 rounded-full capitalize"
                        >
                            {cat}
                        </span>
                    ))}
                </div>

                <motion.h3
                    className="text-xs font-semibold leading-tight line-clamp-2 text-gray-800"
                    variants={{ hovered: { color: 'var(--primary)', transition: { duration: 0.2 } } }}
                >
                    {title}
                </motion.h3>

                {lowest_product.amount ? (
                     <div className="flex flex-col text-left mt-auto">
                        <span className="text-[10px] uppercase text-gray-400 leading-none">
                            À partir de
                        </span>
                        <span className="font-semibold text-sm text-primary-dark leading-tight">
                            {formatAmount(lowest_product.amount)}
                        </span>
                    </div>
                ) : (
                    <span className="text-[10px] text-gray-400 italic mt-auto">Indisponible</span>
                )}
            </div>
        </motion.div>
    )
}

type VendorCardProps = Partial<StoreWithAverageRating>

const VendorCard = ({ name, metadata, averageRating, id }: VendorCardProps) => {
   
    const isMobile = IsMobile();

    return (
        <div className="relative w-full h-full overflow-hidden text-left rounded-lg cursor-pointer group">
            <span
                style={{
                    background: `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #010101 70%)`,
                }}
                className={clsx(
                    'absolute inset-0 transition duration-300 rounded-lg',
                    isMobile
                        ? 'bg-opacity-50 opacity-100'
                        : 'bg-opacity-50 opacity-100 group-hover:opacity-100',
                )}
            />
            <Anchor href={`/stores/${id}`}>
                <div
                    className={clsx(
                        'absolute inset-0 transition duration-300 rounded-lg z-10 p-2  gap-x-2',
                        isMobile
                            ? 'opacity-100'
                            : 'opacity-100 group-hover:opacity-100',
                    )}
                >
                    <div className="absolute top-0 left-0 flex items-end justify-end w-full h-full p-4 rounded-lg">
                        <div className="flex items-end w-full gap-x-4">
                            <div className="grid content-start">
                                <Text
                                    weight={700}
                                    className="mb-1 text-sm text-white uppercase "
                                >
                                    {name}
                                </Text>
                                <Rating
                                    value={averageRating}
                                    readOnly
                                    size="xs"
                                />
                            </div>

                            <Button
                                size="xs"
                                variant="white"
                                color="dark"
                                className="px-2"
                            >
                                <ArrowRight size={16} />
                            </Button>
                        </div>
                    </div>
                </div>
            </Anchor>

            <ImageWithFallback
                src={
                    (metadata?.thumbnail as string) ??
                    '/images/vendors/placeholder.png'
                }
                alt=""
                loading="lazy"
                width={500}
                height={500}
                className="object-cover object-center w-full h-full rounded-lg aspect-square"
            />
        </div>
    )
}

export default withLayout(HomePage)
